import { useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { EmptyState, LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime } from '@/i18n/format';
import type { StockCorrection } from '@/api/types';
import { plainQty, signedFromString, stockReasonLabel } from './shared';

/** Who changed what stock, when and why - newest first. Filter by product is client-side (code or name). */
export function HistoryTab(): JSX.Element {
  const { t, locale } = useLanguage();
  const result = useApi(() => endpoints.stockCorrections(), []);
  const [filter, setFilter] = useState('');

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const needle = filter.trim().toLowerCase();
  const rows = result.data.corrections.filter((c) => !needle || `${c.sku_code} ${c.sku_name}`.toLowerCase().includes(needle));

  const change = (c: StockCorrection): string => {
    if (c.kind === 'COUNT') {
      return c.old_qty !== null && c.new_qty !== null && c.delta !== null
        ? `${plainQty(c.old_qty)} → ${plainQty(c.new_qty)} (${signedFromString(c.delta)})`
        : '—';
    }
    return c.delta !== null ? plainQty(c.delta.replace('-', '')) : '—';
  };

  return (
    <div>
      <div className="filters">
        <input
          aria-label={t('exc.histFilter')}
          placeholder={t('exc.histFilter')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>
      {rows.length === 0 ? (
        <EmptyState title={t('exc.histEmpty')} />
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('exc.colWhen')}</th>
                <th>{t('exc.colWho')}</th>
                <th>{t('exc.colKind')}</th>
                <th>{t('exc.colLocation')}</th>
                <th>{t('exc.colProduct')}</th>
                <th>{t('exc.colSeller')}</th>
                <th>{t('exc.colChange')}</th>
                <th>{t('exc.colReason')}</th>
                <th>{t('exc.colNote')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>{formatDateTime(c.created_at, locale)}</td>
                  <td>{c.actor_name ?? t('exc.handheld')}</td>
                  <td>{t(`exc.kind.${c.kind}`)}</td>
                  <td>{c.to_location_code ? `${c.location_code} → ${c.to_location_code}` : c.location_code}</td>
                  <td>
                    {c.sku_code} — {c.sku_name}
                  </td>
                  <td>{c.owner_name}</td>
                  <td>{change(c)}</td>
                  <td>{stockReasonLabel(t, c.reason_code)}</td>
                  <td>{c.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
